import { isBoolean, isFunction } from 'es-toolkit';

import type { Nullable } from '@clearverify/types';

import { gql } from 'src/utils/gql';

import { useSearch } from './useSearch';

type SearchPredicate = { contains: string };
type SearchByFullText = { _fullText: string };
type SearchQueryVariable = Record<string, SearchPredicate>;
type MultiFieldSearchQuery<SearchQueryVariable> = {
  OR: Array<SearchQueryVariable>;
};
type AnyCustomFilter = Record<string, unknown>;
type SearchCallback<Filter = AnyCustomFilter> = (
  searchValue: Nullable<string>,
  searchCategory?: Nullable<string>,
) => Filter;

function isMultiFieldSearchQuery(searchFields: unknown): searchFields is Array<string> {
  return Array.isArray(searchFields);
}

function isSearchCallback(customFilter: unknown): customFilter is SearchCallback {
  return isFunction(customFilter);
}

/**
 * A component should be wrapped inside a SearchProvider
 * @example
 * with fullText
 * const searchQueryVariables = useSearchQueryVariables('name');
 * searchQueryVariables = {
 *   _fullText: searchValue,
 * };
 */

export function useSearchQueryVariables(withFullText: boolean): SearchByFullText;
/**
 * A component should be wrapped inside a SearchProvider
 * @example
 * with single field
 * const searchQueryVariables = useSearchQueryVariables('name');
 * searchQueryVariables = {
 *   name: {
 *     contains: "search string",
 *   },
 * }
 */
export function useSearchQueryVariables(field: string): SearchQueryVariable;
/**
 * A component should be wrapped inside a SearchProvider
 * @example
 * with multi fields
 * const searchQueryVariables = useSearchQueryVariables(['name', 'city']);
 * searchQueryVariables = {
 *   OR: [
 *     {
 *       name: {
 *         contains: "search string"
 *       },
 *     },
 *     {
 *       city: {
 *         contains: "search string"
 *       },
 *     },
 *   ]
 * }
 */
export function useSearchQueryVariables(
  fields: Array<string>,
): MultiFieldSearchQuery<SearchQueryVariable>;

export function useSearchQueryVariables<Filter = AnyCustomFilter>(
  searchCallback: SearchCallback<Filter>,
): Filter;

export function useSearchQueryVariables(
  searchParam: string | Array<string> | SearchCallback | boolean,
) {
  const { searchValue, searchCategory } = useSearch();

  const buildMultiFieldSearchQueryVariables = (searchValue: Nullable<string>) => {
    const searchByFields = (searchParam as Array<string>).map(field => {
      return gql.contains(field, searchValue);
    });

    return gql.wrapToOR(searchByFields);
  };

  const buildQueryVariables = (searchValue: Nullable<string>) => {
    if (!searchValue) {
      return {};
    }

    if (isBoolean(searchParam)) {
      return {
        _fullText: searchValue,
      };
    }

    if (isMultiFieldSearchQuery(searchParam)) {
      return buildMultiFieldSearchQueryVariables(searchValue);
    }

    if (isSearchCallback(searchParam)) {
      return searchParam(searchValue, searchCategory);
    }

    return gql.contains(searchParam, searchValue);
  };

  return buildQueryVariables(searchValue);
}
